<template>
    <div class="rd">
        <div class="pagina">
            <header class="paginakop">
                <div class="tekst">
                    <h1>Contacts</h1>
                    <p>
                        People at your locations who get the X-Cube newsletter. Turn on
                        monitoring and they also get a message when an X-Cube has a technical
                        problem.
                    </p>
                </div>
                <div class="rechts">
                    <button v-if="!busy" type="button" class="btn btn-paars btn-sm" @click="startNew">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M12 5v14M5 12h14" /></svg>
                        Add contact
                    </button>
                </div>
            </header>

            <div v-if="unnamedCount" class="melding-blok info" role="note">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="3.6" /><path d="M4.5 20c0-3.6 3.4-6 7.5-6s7.5 2.4 7.5 6" /></svg>
                <div>
                    <b>{{ unnamedCount }} contacts have no name.</b> Add a name so locations
                    know who gets the alerts. Contacts without a name show as "No name".
                </div>
            </div>

            <section class="paneel filters">
                <div class="filter-rij">
                    <div class="veld f-loc">
                        <label for="co-loc">Location</label>
                        <select id="co-loc" v-model="locationFilter" @change="reloadList">
                            <option v-for="opt in locationFilterOptions" :key="opt.id" :value="opt.id">
                                {{ opt.name }}
                            </option>
                        </select>
                    </div>
                    <label class="zoek" style="align-self: flex-end">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></svg>
                        <input
                            v-model="searchQuery"
                            type="search"
                            placeholder="Search name, email or phone"
                            aria-label="Search contacts"
                        />
                    </label>
                    <button
                        type="button"
                        class="chip"
                        style="align-self: flex-end"
                        :aria-pressed="monitoringOnly"
                        @click="monitoringOnly = !monitoringOnly"
                    >
                        Monitoring only
                    </button>
                </div>
            </section>

            <section v-if="busy" class="paneel">
                <div class="paneel-kop">
                    <h2>{{ isEditing ? "Edit contact" : "Add contact" }}</h2>
                </div>

                <div v-if="loadingDetail" class="laadregel">Loading contact…</div>

                <form v-else novalidate @submit.prevent="save">
                    <div class="twee-kol">
                        <div class="veld">
                            <label for="co-first">First name</label>
                            <input id="co-first" v-model="form.first_name" type="text" autocomplete="off" />
                        </div>
                        <div class="veld">
                            <label for="co-last">Last name</label>
                            <input id="co-last" v-model="form.last_name" type="text" autocomplete="off" />
                        </div>
                    </div>
                    <div class="veld">
                        <label for="co-mail">Email</label>
                        <input
                            id="co-mail"
                            v-model="form.emailadres"
                            type="email"
                            autocomplete="off"
                            placeholder="ruben@example.com"
                            :aria-invalid="errors.mail ? 'true' : 'false'"
                        />
                        <span class="fout">{{ errors.mail }}</span>
                    </div>
                    <div class="twee-kol">
                        <div class="veld">
                            <label for="co-mobile">Mobile</label>
                            <input id="co-mobile" v-model="form.telephone_mobile" type="tel" autocomplete="off" placeholder="+31 6 12345678" />
                        </div>
                        <div class="veld">
                            <label for="co-locphone">Location phone</label>
                            <input id="co-locphone" v-model="form.telephone_location" type="tel" autocomplete="off" />
                        </div>
                    </div>
                    <div class="twee-kol">
                        <div class="veld">
                            <label for="co-function">Function</label>
                            <select id="co-function" v-model="form.function">
                                <option value="">—</option>
                                <option v-for="fn in functionOptions" :key="fn" :value="fn">{{ fn }}</option>
                            </select>
                        </div>
                        <div class="veld">
                            <label for="co-language">Language</label>
                            <select id="co-language" v-model="form.Language">
                                <option value="">—</option>
                                <option v-for="lang in languageOptions" :key="lang.value" :value="lang.value">
                                    {{ lang.label }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="veld">
                        <label id="co-loc-l">
                            Locations
                            <span class="muted" style="font-weight: 400; text-transform: none; letter-spacing: 0">
                                · a contact can belong to more than one
                            </span>
                        </label>
                        <div
                            class="loc-picker"
                            :class="{ 'fout-rand': errors.loc }"
                            role="group"
                            aria-labelledby="co-loc-l"
                        >
                            <label v-for="loc in sortedPickerLocations" :key="loc.id" class="aanvink">
                                <input
                                    v-model="form.locationIds"
                                    type="checkbox"
                                    :value="loc.id"
                                />
                                {{ loc.name }}
                            </label>
                            <p v-if="pickerLocations.length === 0" class="hint">No locations in your account.</p>
                        </div>
                        <span class="fout">{{ errors.loc }}</span>
                    </div>

                    <div class="schakel" style="border: 0; padding: 0 0 16px">
                        <span class="t">
                            <span>Monitoring alerts</span>
                            <small>Text message and email when the X-Cube goes offline or a game crashes</small>
                        </span>
                        <button
                            type="button"
                            class="switch"
                            role="switch"
                            :aria-checked="form.receive_monitoring ? 'true' : 'false'"
                            :disabled="monitoringLocked"
                            @click="form.receive_monitoring = !form.receive_monitoring"
                        />
                    </div>
                    <p v-if="monitoringLocked" class="hint" style="margin: -8px 0 16px">
                        This is the only monitoring contact left for at least one location, so it
                        cannot be turned off here.
                    </p>

                    <div v-if="confirmingDeactivate" class="bevestig" style="margin-bottom: 16px">
                        <span>This contact will be set inactive. It will not be deleted.</span>
                        <div class="knoprij">
                            <button type="button" class="btn btn-rood btn-sm" :disabled="deactivating" @click="doDeactivate">
                                Yes, deactivate
                            </button>
                            <button type="button" class="btn btn-stil btn-sm" @click="confirmingDeactivate = false">
                                Keep contact
                            </button>
                        </div>
                    </div>

                    <div class="knoprij">
                        <button type="submit" class="btn btn-paars" :disabled="saving">
                            {{ isEditing ? "Save changes" : "Add contact" }}
                        </button>
                        <button type="button" class="btn btn-stil" @click="closeEditor">Cancel</button>
                        <button
                            v-if="isEditing && !confirmingDeactivate"
                            type="button"
                            class="btn btn-ghost"
                            style="margin-left: auto"
                            @click="confirmingDeactivate = true"
                        >
                            Deactivate
                        </button>
                    </div>
                </form>
            </section>

            <section class="paneel">
                <div v-if="loadingList" class="laadregel">Loading contacts…</div>
                <template v-else-if="filteredContacts.length">
                    <div class="tabel-wikkel">
                        <table class="tabel">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Locations</th>
                                    <th>Monitoring</th>
                                    <th><span hidden>Actions</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="c in filteredContacts" :key="c.id">
                                    <td>
                                        <span v-if="contactName(c)">{{ contactName(c) }}</span>
                                        <span v-else class="leegwaarde">No name</span>
                                    </td>
                                    <td>
                                        <span v-if="c.emailadres">{{ c.emailadres }}</span>
                                        <span v-else class="leegwaarde">–</span>
                                    </td>
                                    <td class="num nowrap">{{ formatContactPhone(c) }}</td>
                                    <td>{{ (c.locationNames || []).join(", ") }}</td>
                                    <td>
                                        <button
                                            class="switch"
                                            type="button"
                                            role="switch"
                                            :disabled="togglingId === c.id"
                                            :aria-checked="c.receive_monitoring ? 'true' : 'false'"
                                            :aria-label="`Monitoring for ${contactName(c) || formatContactPhone(c)}`"
                                            @click="toggleMonitoring(c)"
                                        />
                                    </td>
                                    <td class="rechts">
                                        <button
                                            type="button"
                                            class="btn btn-stil btn-icoon"
                                            :aria-label="`Edit ${contactName(c) || formatContactPhone(c)}`"
                                            @click="editContact(c)"
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17v3z" /><path d="M13.5 6.5l3 3" /></svg>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="tabel-voet">
                        <span>
                            {{ filteredContacts.length }} contacts ·
                            {{ filteredContacts.filter((c) => c.receive_monitoring).length }} with monitoring
                        </span>
                    </div>
                </template>
                <div v-else class="leeg">
                    <h2>{{ searchQuery ? "No contact matches your search" : "No contacts here yet" }}</h2>
                    <p>
                        {{
                            searchQuery
                                ? "Search on part of a name, email address or phone number."
                                : "Add a contact so this location gets the newsletter and alerts."
                        }}
                    </p>
                </div>
            </section>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { Message } from "element-ui";
import {
    createContact,
    deactivateContact,
    emptyContactForm,
    getContact,
    listContacts,
    updateContact,
} from "../../api/contacts";
import { fetchUserData } from "../../api/graphql/users";
import { normalizeLanguageOptions } from "../../helpers/contactLanguage";
import { formatContactName, formatContactPhone } from "../../helpers/contactName";
import { sortLocationsByName } from "../../helpers/locations";
import useCurrentUserStore from "../../store/userStore";

const store = useCurrentUserStore();

const contacts = ref([]);
const pickerLocations = ref([]);
// BFF-sourced locations (not the Hub store); same shared order as every other picker.
const sortedPickerLocations = computed(() => sortLocationsByName(pickerLocations.value));
const functionOptions = ref([]);
const languageOptions = ref([]);
const loadingList = ref(false);
const loadingDetail = ref(false);
const saving = ref(false);
const deactivating = ref(false);
const togglingId = ref(null);
const confirmingDeactivate = ref(false);
const locationFilter = ref("all");
const searchQuery = ref("");
const monitoringOnly = ref(false);
const selectedId = ref(null);
const isNew = ref(false);
const isEditing = ref(false);
const canUnsetMonitoring = ref(true);
const form = ref(null);
let loadSeq = 0;

const busy = computed(() => isNew.value || isEditing.value);
const errors = ref({ mail: "", loc: "" });

const hubLocations = computed(() => store.currentUser?.locations || []);

const locationFilterOptions = computed(() => [
    { id: "all", name: "All locations" },
    ...hubLocations.value.map((loc) => ({ id: String(loc.id), name: loc.name })),
]);

const contactName = (c) => {
    const name = formatContactName(c);
    // formatContactName falls back to the "no name" sentinel; the redesign
    // renders that as a muted placeholder, so treat it as empty here.
    return name.toLowerCase() === "no name" ? "" : name;
};

const unnamedCount = computed(
    () => contacts.value.filter((c) => !contactName(c)).length,
);

const monitoringLocked = computed(
    () => !!form.value?.receive_monitoring && !canUnsetMonitoring.value,
);

const filteredContacts = computed(() => {
    const q = searchQuery.value.trim().toLowerCase();
    return contacts.value
        .filter((c) => !monitoringOnly.value || c.receive_monitoring)
        .filter((c) => {
            if (!q) return true;
            const hay = [
                c.label,
                c.first_name,
                c.last_name,
                c.emailadres,
                c.telephone_mobile,
                c.telephone_location,
                c.function,
                ...(c.locationNames || []),
            ]
                .join(" ")
                .toLowerCase();
            return hay.includes(q);
        })
        .sort((a, b) =>
            (a.locationNames?.[0] || "").localeCompare(b.locationNames?.[0] || ""),
        );
});

const showError = (error) => {
    Message.error({ message: error?.message || "Something went wrong" });
};

const reloadList = async () => {
    loadingList.value = true;
    try {
        const data = await listContacts({ locationId: locationFilter.value });
        contacts.value = data.contacts || [];
        pickerLocations.value = data.locations || [];
        functionOptions.value = data.fieldOptions?.function || [];
        languageOptions.value = normalizeLanguageOptions(data.fieldOptions?.language);
    } catch (error) {
        showError(error);
    } finally {
        loadingList.value = false;
    }
};

const closeEditor = () => {
    loadSeq += 1;
    form.value = null;
    selectedId.value = null;
    isNew.value = false;
    isEditing.value = false;
    loadingDetail.value = false;
    confirmingDeactivate.value = false;
    errors.value = { mail: "", loc: "" };
};

const startNew = () => {
    closeEditor();
    isNew.value = true;
    canUnsetMonitoring.value = true;
    const next = emptyContactForm();
    if (pickerLocations.value.length === 1) {
        next.locationIds = [pickerLocations.value[0].id];
    }
    form.value = next;
    window.scrollTo(0, 0);
};

const editContact = async (row) => {
    if (!row?.id) return;
    // closeEditor() bumps loadSeq, so capture the sequence number *after* it;
    // otherwise the in-flight guard below always trips and the form never loads.
    closeEditor();
    const seq = ++loadSeq;
    isEditing.value = true;
    selectedId.value = row.id;
    loadingDetail.value = true;
    window.scrollTo(0, 0);
    try {
        const data = await getContact(row.id);
        if (seq !== loadSeq) return;
        form.value = data.form;
        canUnsetMonitoring.value = data.can_unset_monitoring !== false;
        if (data.locations) pickerLocations.value = data.locations;
        if (data.fieldOptions) {
            functionOptions.value = data.fieldOptions.function || functionOptions.value;
            languageOptions.value = normalizeLanguageOptions(
                data.fieldOptions.language || languageOptions.value,
            );
        }
    } catch (error) {
        if (seq !== loadSeq) return;
        showError(error);
        closeEditor();
    } finally {
        if (seq === loadSeq) loadingDetail.value = false;
    }
};

const validate = () => {
    const next = { mail: "", loc: "" };
    const email = (form.value.emailadres || "").trim();
    const mobile = (form.value.telephone_mobile || "").trim();
    if (!form.value.locationIds?.length) {
        next.loc = "Choose at least one location this contact belongs to.";
    }
    if (!email && !mobile) {
        next.mail = "Enter an email address or a mobile number, so the contact can be reached.";
    } else if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        next.mail = "This email address is missing an @ or a domain.";
    }
    errors.value = next;
    return !next.mail && !next.loc;
};

const save = async () => {
    if (!form.value || !validate()) return;
    saving.value = true;
    try {
        const creating = isNew.value;
        if (creating) {
            await createContact(form.value);
        } else {
            await updateContact(selectedId.value, form.value);
        }
        Message.success({ message: creating ? "Contact created" : "Contact saved" });
        closeEditor();
        await reloadList();
    } catch (error) {
        showError(error);
    } finally {
        saving.value = false;
    }
};

const toggleMonitoring = async (c) => {
    if (togglingId.value) return;
    const next = !c.receive_monitoring;
    togglingId.value = c.id;
    c.receive_monitoring = next; // optimistic; the BFF stays authoritative
    try {
        const { form: full } = await getContact(c.id);
        await updateContact(c.id, { ...full, receive_monitoring: next });
        Message.success({ message: next ? "Monitoring on" : "Monitoring off" });
    } catch (error) {
        c.receive_monitoring = !next; // revert on rejection (e.g. last monitoring contact)
        showError(error);
    } finally {
        togglingId.value = null;
    }
};

const doDeactivate = async () => {
    if (!selectedId.value) return;
    deactivating.value = true;
    try {
        await deactivateContact(selectedId.value);
        Message.success({ message: "Contact deactivated" });
        closeEditor();
        await reloadList();
    } catch (error) {
        showError(error);
    } finally {
        deactivating.value = false;
    }
};

onMounted(async () => {
    if (!store.currentUser?.locations) {
        await fetchUserData();
    }
    await reloadList();
});
</script>
